<template>
  <BaseModal
    :dialog.sync="dialog"
    @close="handleClose"
    :title="modalTitle"
    :key="ticketData.idproduto_variacao"
  >
    <v-form @submit.prevent ref="form">
      <v-row>
        <v-col cols="12" v-if="getIsRecurringEvent">
          <TopicTitle
            :icon="'mdi-calendar'"
            :title="
              $t('new-event.ticket-management.tickets.modal.sales-period')
            "
            :subtitle="
              $t(
                'new-event.ticket-management.tickets.modal.sales-period-description'
              )
            "
          />
          <v-row>
            <v-col cols="12" md="6">
              <DateTimePicker
                :label="
                  $t('new-event.ticket-management.recharges.fields.sales-start')
                "
                :edit="ticketData.salesPeriodStart"
                @formatted-date="saleStartFormattedDate"
                :key="
                  ticketData.salesPeriodStart +
                  ticketData.idproduto_variacao +
                  'start'
                "
              />
            </v-col>
            <v-col cols="12" md="6">
              <DateTimePicker
                :label="
                  $t('new-event.ticket-management.recharges.fields.sales-end')
                "
                :edit="ticketData.salesPeriodEnd"
                @formatted-date="saleEndFormattedDate"
                :key="
                  ticketData.salesPeriodEnd +
                  ticketData.idproduto_variacao +
                  'end'
                "
                v-if="!isBulkCurrentEvent"
                :allowed-dates="allowedPeriodEndSaleDates"
              />
              <BaseTextField
                v-else
                :label="$t('new-event.basic-information.inputs.sale-hour-end')"
                placeholder="0"
                v-model="ticketData.salesEndHoursAfter"
                autocomplete="new"
                type="number"
                persistent-hint
                :hint="
                  $t('new-event.basic-information.inputs.sale-hour-end-hint')
                "
                hide-details="auto"
                :rules="[rules.totalRequired, rules.nonNegativeNumber]"
                hide-spin-buttons
                @wheel.prevent
                @focus="$event.target.select()"
              />
            </v-col>
          </v-row>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" md="6">
          <TopicTitle
            :icon="'mdi-store-outline'"
            :title="
              $t('new-event.ticket-management.tickets.modal.sales-channel')
            "
            :subtitle="
              $t(
                'new-event.ticket-management.tickets.modal.sales-channel-description'
              )
            "
          />
          <BaseSelect
            :items="filtredSalesChannel()"
            :label="
              $t(
                'new-event.ticket-management.tickets.modal.inputs.sales-channel'
              )
            "
            v-model="ticketData.salesChannel"
            class="self-end"
            item-text="value"
            item-value="key"
            :key="ticketData.idproduto_variacao + '-sales-channel'"
            :disabled="ticketData.isReservation || ticketData.isGratuity"
          />
        </v-col>
        <v-col cols="12" md="6">
          <TopicTitle
            :icon="'mdi-ticket-confirmation-outline'"
            :title="$t('new-event.ticket-management.tickets.modal.block')"
            :subtitle="
              $t('new-event.ticket-management.tickets.modal.block-description')
            "
          />
          <BaseTextField
            :label="
              $t(
                'new-event.ticket-management.tickets.modal.inputs.block-quantity'
              )
            "
            type="number"
            :rules="[
              rules.nonNegativeNumber,
              blockQuantityCantBeMoreThanQuantity,
            ]"
            hide-spin-buttons
            v-model="ticketData.blockQuantity"
            :key="ticketData.idproduto_variacao + '-block-quantity'"
            :hint="hintBlockTickets"
            persistent-hint
            @wheel.prevent
            @focus="$event.target.select()"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" md="6">
          <TopicTitle
            :icon="'mdi-text'"
            :title="$t('new-event.ticket-management.tickets.modal.desc')"
            :subtitle="
              $t('new-event.ticket-management.tickets.modal.desc-description')
            "
          />
          <BaseTextarea
            persistent-placeholder
            :label="`${$t(
              'new-event.ticket-management.tickets.modal.inputs.description'
            )} ${
              selectedLocales.length > 0 ? `(${getTextLocale(locale)})` : ''
            }`"
            v-model="descriptionDefault"
            :key="ticketData.idproduto_variacao + '-description-default-locale'"
            counter="500"
            maxlength="500"
          />
          <v-col
            v-for="(locale, index) in selectedLocales"
            :key="index"
            cols="12"
            class="pa-0 ma-0 mt-4"
          >
            <BaseTextarea
              persistent-placeholder
              :label="`${$t(
                'new-event.ticket-management.tickets.modal.inputs.description'
              )} (${getTextLocale(locale)})`"
              v-model="descriptionLocales[locale]"
              :id="`description-${index}`"
              :key="ticketData.idproduto_variacao + '-description-' + index"
              counter="500"
              maxlength="500"
            />
          </v-col>
        </v-col>
        <v-col cols="12" md="6">
          <TopicTitle
            :icon="'mdi-ticket-confirmation-outline'"
            :title="
              $t('new-event.ticket-management.tickets.modal.period-access')
            "
            :subtitle="
              $t(
                'new-event.ticket-management.tickets.modal.period-access-description'
              )
            "
          />
          <div v-if="isBulkCurrentEvent && getIsRecurringEvent">
            <BaseTextField
              :label="
                $t(
                  'new-event.ticket-management.tickets.modal.inputs.check-in-recurring'
                )
              "
              placeholder="0"
              type="number"
              v-model="ticketData.accessPeriodStart"
              class="mt-4"
              :rules="[rules.nonNegativeNumber]"
              hide-spin-buttons
              @wheel.prevent
              hide-details="auto"
              @focus="$event.target.select()"
            />
            <div class="mt-4">
              <BaseTextField
                :label="
                  $t(
                    'new-event.ticket-management.tickets.modal.inputs.check-out-recurring'
                  )
                "
                placeholder="0"
                type="number"
                v-model="ticketData.accessPeriodEnd"
                class="mt-4"
                :rules="[rules.nonNegativeNumber]"
                hide-spin-buttons
                @wheel.prevent
                hide-details="auto"
                @focus="$event.target.select()"
              />
            </div>
          </div>
          <div v-else>
            <DateTimePicker
              :label="
                $t('new-event.ticket-management.tickets.modal.inputs.check-in')
              "
              :edit="ticketData.accessPeriodStart"
              @formatted-date="accessPeriodStartFormattedDate"
              :key="ticketData.accessPeriodStart + 'access-period-start'"
            />
            <div class="mt-4">
              <DateTimePicker
                :label="
                  $t(
                    'new-event.ticket-management.tickets.modal.inputs.check-out'
                  )
                "
                :edit="ticketData.accessPeriodEnd"
                @formatted-date="accessPeriodEndFormattedDate"
                :key="ticketData.accessPeriodEnd + 'access-period-end'"
              />
            </div>
          </div>
          <BaseTextField
            :label="
              $t('new-event.ticket-management.tickets.modal.inputs.use-limits')
            "
            :hint="
              $t('new-event.ticket-management.tickets.modal.hint-use-limits')
            "
            persistent-hint
            type="number"
            v-model="ticketData.accessQuantity"
            class="mt-4"
            :key="ticketData.idproduto_variacao + '-access-quantity'"
            :rules="[rules.nonNegativeNumber]"
            hide-spin-buttons
            @wheel.prevent
            @focus="$event.target.select()"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" md="6">
          <TopicTitle
            :icon="'mdi-ticket-confirmation-outline'"
            :title="
              $t(
                'new-event.ticket-management.tickets.modal.courtesy-and-gratuity'
              )
            "
            :subtitle="
              $t(
                'new-event.ticket-management.tickets.modal.courtesy-and-gratuity-description'
              )
            "
          />
          <v-row no-gutters>
            <v-col class="d-flex pa-0 ma-0">
              <v-checkbox
                :label="
                  $t(
                    'new-event.ticket-management.tickets.modal.inputs.courtesy'
                  )
                "
                v-model="ticketData.isCourtesy"
                @change="onCourtesyChange"
                hide-details
                dense
              />
            </v-col>
            <v-col class="d-flex pa-0 ma-0">
              <v-checkbox
                :label="
                  $t('new-event.ticket-management.tickets.modal.gratuity')
                "
                v-model="ticketData.isGratuity"
                @change="onGratuityChange"
                hide-details
                dense
              />
            </v-col>
          </v-row>
        </v-col>
        <v-col
          cols="12"
          md="6"
          class="d-flex flex-column justify-space-between h-100"
        >
          <TopicTitle
            :icon="'mdi-calendar-multiple'"
            :title="
              $t('new-event.ticket-management.tickets.modal.change-batch')
            "
            :subtitle="
              $t(
                'new-event.ticket-management.tickets.modal.change-batch-description'
              )
            "
          />
          <v-col class="pa-0 ma-0 mb-4">
            <DateTimePicker
              :label="
                $t(
                  'new-event.ticket-management.tickets.modal.inputs.dttm-change-batch'
                )
              "
              :edit="ticketData.dateChangeBatch"
              :allowed-dates="allowedChangeBatchDate"
              @formatted-date="dateChangeBatchFormattedDate"
              :key="ticketData.dateChangeBatch + ticketData.idproduto_variacao"
              :disabled="ticketData.batch === 0"
            />
          </v-col>
          <BaseSelect
            :items="eventWithoutCurrentEvent"
            :label="
              $t(
                'new-event.ticket-management.tickets.modal.inputs.previous-batch'
              )
            "
            item-value="idproduto_variacao"
            :item-text="formatItemText"
            v-model="ticketData.previousBatch"
            :key="ticketData.previousBatch + ticketData.idproduto_variacao"
            disabled
          />
        </v-col>
      </v-row>
      <v-row v-if="isAdmin">
        <v-col cols="12" md="6">
          <TopicTitle
            :icon="'mdi-calendar-check-outline'"
            :title="$t('new-event.ticket-management.tickets.modal.reservation')"
            :subtitle="
              $t(
                'new-event.ticket-management.tickets.modal.reservation-description'
              )
            "
          />
          <v-col class="d-flex pa-0 ma-0">
            <v-checkbox
              :label="
                $t('new-event.ticket-management.tickets.modal.reservation')
              "
              v-model="ticketData.isReservation"
              @change="onReservationChange"
              hide-details
              dense
            />
          </v-col>
        </v-col>
        <v-col cols="12" md="6">
          <TopicTitle
            :title="
              $t(
                'new-event.ticket-management.tickets.modal.integration-complement'
              )
            "
            :subtitle="
              $t(
                'new-event.ticket-management.tickets.modal.integration-complement-description'
              )
            "
            icon="mdi-tune-vertical"
          />
          <BaseTextField
            v-model="ticketData.integrationComplement"
            :label="
              $t(
                'new-event.ticket-management.tickets.modal.inputs.integration-complement'
              )
            "
          />
        </v-col>
      </v-row>
      <v-row v-if="isAdmin">
        <v-col cols="12" class="pb-0">
          <TopicTitle
            class="mb-4"
            :title="$t('new-event.ticket-management.tickets.modal.comission')"
            icon="mdi-cash-refund"
            :subtitle="
              $t(
                'new-event.ticket-management.tickets.modal.comission-description'
              )
            "
          />
        </v-col>
        <v-col sm="6" cols="12" class="pt-0">
          <BaseSelect
            class="mb-4"
            :items="commissionType"
            item-text="item"
            item-value="value"
            :label="`${$t(
              'new-event.ticket-management.tickets.modal.inputs.comission'
            )} Web`"
            persistent-placeholder
            v-model="ticketData.commissionWeb"
            :key="ticketData.idproduto_variacao + '-comission-web'"
            :disabled="ticketData.isReservation || ticketData.isGratuity"
          ></BaseSelect>
          <v-row class="mt-4">
            <v-col cols="12" md="6">
              <BaseSelect
                item-value="value"
                item-text="item"
                :items="typeTax"
                :label="`${$t(
                  'new-event.ticket-management.tickets.modal.inputs.type-tax'
                )} WEB`"
                persistent-placeholder
                v-model="ticketData.typeTaxWeb"
                :key="ticketData.idproduto_variacao + '-type-tax-web'"
                :disabled="ticketData.isReservation || ticketData.isGratuity"
              />
            </v-col>
            <v-col cols="12" md="6">
              <BaseMoneyField
                v-if="ticketData.typeTaxWeb === 'percent'"
                class="mb-4"
                v-model="ticketData.taxWeb"
                :label="`${$t(
                  'new-event.ticket-management.tickets.modal.inputs.tax'
                )} (%)`"
                persistent-placeholder
                :options="optionsPercent"
                :key="ticketData.idproduto_variacao + '-tax-web'"
                :rules="[rules.maxLimit]"
                :disabled="ticketData.isReservation || ticketData.isGratuity"
                selectContent
              />
              <BaseMoneyField
                v-else
                class="mb-4"
                v-model="ticketData.taxWeb"
                :label="`${$t(
                  'new-event.ticket-management.tickets.modal.inputs.tax'
                )} (R$)`"
                persistent-placeholder
                :options="optionsCurrency"
                :key="ticketData.idproduto_variacao + '-tax-web-2'"
                :disabled="ticketData.isReservation || ticketData.isGratuity"
                selectContent
              />
            </v-col>
          </v-row>
          <BaseMoneyField
            v-model="ticketData.minimumTax"
            :label="`${$t(
              'new-event.ticket-management.tickets.modal.inputs.min-tax'
            )} (R$)`"
            persistent-placeholder
            :options="optionsCurrency"
            :key="ticketData.idproduto_variacao + '-min-tax'"
            :disabled="ticketData.isReservation || ticketData.isGratuity"
            selectContent
          />
        </v-col>
        <v-col sm="6" cols="12" class="pt-0">
          <BaseSelect
            class="mb-4"
            :items="commissionType"
            :label="`${$t(
              'new-event.ticket-management.tickets.modal.inputs.comission'
            )} POS`"
            persistent-placeholder
            item-text="item"
            item-value="value"
            v-model="ticketData.commissionPos"
            :key="ticketData.idproduto_variacao + '-comission-pos'"
            :disabled="ticketData.isReservation || ticketData.isGratuity"
          ></BaseSelect>
          <v-row class="mt-4">
            <v-col cols="12" md="6">
              <BaseSelect
                item-text="item"
                item-value="value"
                :items="typeTax"
                :label="`${$t(
                  'new-event.ticket-management.tickets.modal.inputs.type-tax'
                )} POS`"
                persistent-placeholder
                v-model="ticketData.typeTaxPos"
                :key="ticketData.idproduto_variacao + '-type-tax-pos-2'"
                :disabled="ticketData.isReservation || ticketData.isGratuity"
              />
            </v-col>
            <v-col cols="12" md="6">
              <BaseMoneyField
                v-if="ticketData.typeTaxPos === 'percent'"
                v-model="ticketData.taxPos"
                :label="`${$t(
                  'new-event.ticket-management.tickets.modal.inputs.tax'
                )} (%)`"
                persistent-placeholder
                :options="optionsPercent"
                :key="ticketData.idproduto_variacao + '-tax-pos'"
                :rules="[rules.maxLimit]"
                :disabled="ticketData.isReservation || ticketData.isGratuity"
                selectContent
              />
              <BaseMoneyField
                v-else
                v-model="ticketData.taxPos"
                :label="`${$t(
                  'new-event.ticket-management.tickets.modal.inputs.tax'
                )} (R$)`"
                persistent-placeholder
                :options="optionsCurrency"
                :key="ticketData.idproduto_variacao + '-tax-pos-2'"
                :disabled="ticketData.isReservation || ticketData.isGratuity"
                selectContent
              />
            </v-col>
          </v-row>
          <v-col cols="12" class="pl-0 ma-0 d-flex align-center">
            <v-checkbox
              hide-details
              :label="
                $t(
                  'new-event.ticket-management.tickets.modal.inputs.has-process-tax'
                )
              "
              v-model="ticketData.hasProcessingTax"
              :key="ticketData.idproduto_variacao + 'has-process-tax'"
              :disabled="ticketData.isReservation || ticketData.isGratuity"
            />
          </v-col>
          <v-col v-if="ticketData.hasProcessingTax" cols="12" class="pl-0">
            <BaseMoneyField
              :options="optionsCurrency"
              v-model="ticketData.processingTax"
              :label="`${$t(
                'new-event.ticket-management.tickets.modal.inputs.process-tax'
              )} (R$)`"
              persistent-placeholder
              :key="ticketData.idproduto_variacao + 'processing-tax'"
              :disabled="ticketData.isReservation || ticketData.isGratuity"
              selectContent
            />
          </v-col>
        </v-col>
      </v-row>
      <!-- <v-row>
      <v-col cols="12" md="6" class="d-flex flex-column justify-end h-100">
        <TopicTitle
          :icon="'mdi-collage'"
          :title="$t('new-event.ticket-management.tickets.modal.template')"
          :subtitle="
            $t('new-event.ticket-management.tickets.modal.template-description')
          "
        />
        <BaseFileInput
          :label="`${$t(
            'new-event.ticket-management.tickets.modal.inputs.template'
          )} WEB`"
          placeholder="Inserir documento .odt"
          v-model="ticketData.templateTicketWeb"
          persistent-placeholder
          @change="handleFileChange('WEB', $event)"
          :key="ticketData.idproduto_variacao + 'template-ticket-web'"
        />
        <BaseFileInput
          :label="`${$t(
            'new-event.ticket-management.tickets.modal.inputs.template'
          )} POS`"
          placeholder="Inserir documento .odt"
          v-model="ticketData.templateTicketPos"
          persistent-placeholder
          class="mt-4"
          @change="handleFileChange('POS', $event)"
          :key="ticketData.idproduto_variacao + 'template-ticket-pos-2'"
        />
      </v-col>
    </v-row> -->
      <v-row v-if="selectedLocales.length > 0">
        <v-col cols="12">
          <TopicTitle
            :icon="'mdi-translate'"
            :title="$t('new-event.ticket-management.tickets.modal.translate')"
            :subtitle="
              $t(
                'new-event.ticket-management.tickets.modal.translate-description'
              )
            "
          />
        </v-col>
        <v-col
          v-for="(locale, index) in selectedLocales"
          :key="index"
          cols="12"
          md="6"
          class="d-flex flex-column justify-end h-100"
        >
          <BaseTextField
            :label="getTextLocale(locale)"
            v-model="nameLocales[locale]"
            v-if="nameLocales"
            maxLength="65"
            :key="ticketData.idproduto_variacao + 'translate-' + index"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" md="6">
          <TopicTitle
            :icon="'mdi-eye-settings-outline'"
            :title="$t('new-event.ticket-management.tickets.modal.visibility')"
            :subtitle="
              $t(
                'new-event.ticket-management.tickets.modal.visibility-ticket-description'
              )
            "
          />
            <v-checkbox
              v-model="ticketData.comboOnly"
              :label="
                $t(
                  'new-event.ticket-management.tickets.modal.inputs.ticket-combo-only'
                )
              "
              hide-details
              dense
            />
          
        </v-col>
        <v-col cols="12" md="6">
          <TopicTitle
            :icon="'mdi-face-recognition'"
            :title="$t('new-event.ticket-management.tickets.modal.facial')"
            :subtitle="
              $t('new-event.ticket-management.tickets.modal.facial-description')
            "
          />
          <div class="d-flex">
            <v-checkbox
              v-model="ticketData.ticketFacial"
              :label="
                $t(
                  'new-event.ticket-management.tickets.modal.inputs.ticket-facial'
                )
              "
              hide-details
              dense
              @change="onFacialChange"
              class="pr-8 mt-0"
            />
            <v-checkbox
              v-model="ticketData.ticketFacialRequired"
              :label="
                $t(
                  'new-event.ticket-management.tickets.modal.inputs.facial-required'
                )
              "
              hide-details
              dense
              class="mt-0"
              @change="onFacialRequiredChange"
            />
          </div>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" class="d-flex justify-end mt-2 flex-wrap">
          <v-btn
            class="rounded-pill primary--text mr-2 mb-2"
            outlined
            @click="handleClose"
          >
            {{ $t("new-event.buttons.cancel") }}
          </v-btn>
          <v-btn
            class="rounded-pill primary"
            variant="flat"
            @click="handleSave"
            :loading="getModalLoadingEvent"
            >{{ $t("new-event.buttons.confirm-adjusts") }}</v-btn
          >
        </v-col>
      </v-row>
    </v-form>
  </BaseModal>
</template>

<script>
import BaseModal from "@/components/events/EventSettingsModal/BaseModal.vue";
import TopicTitle from "../../TopicTitle.vue";
import BaseSelect from "../../forms/BaseSelect.vue";
import DateTimePicker from "../../forms/DateTimePicker.vue";
import BaseTextField from "../../forms/BaseTextField.vue";
import BaseTextarea from "../../forms/BaseTextarea.vue";
// import BaseFileInput from "../../forms/BaseFileInput.vue";
import BaseMoneyField from "../../forms/BaseMoneyField.vue";
import {
  getTextLocale,
  processEventName,
  optionsCurrency,
  optionsPercent,
  filtredSalesChannel,
} from "@/utils";
import { mapGetters, mapActions } from "vuex";
import mixins from "@/mixins";

const { formRules } = mixins;

export default {
  components: {
    BaseModal,
    TopicTitle,
    DateTimePicker,
    BaseSelect,
    BaseTextField,
    BaseTextarea,
    // BaseFileInput,
    BaseMoneyField,
  },
  mixins: [formRules],
  props: {
    dialog: {
      type: Boolean,
      default: false,
    },
    eventData: {
      type: Object,
    },
    isBulkEvent: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      filtredSalesChannel,
      form: null,
      optionsCurrency,
      optionsPercent,
      getTextLocale,
      ticketData: {},
      nameLocales: {},
      descriptionDefault: "",
      descriptionLocales: {},
      blockQuantityCantBeMoreThanQuantity: value => {
        const isValidNumber =
          value !== undefined && value !== null && +value >= 0;
        if (isValidNumber && +value <= this.ticketData.quantity) {
          return true;
        }
        return this.$t("common.block-quantity-bigger-than-stock");
      },
    };
  },
  computed: {
    ...mapGetters([
      "getTicketList",
      "getSelectedLocales",
      "isAdmin",
      "getEventBasicData",
      "getIsRecurringEvent",
      "getModalLoadingEvent",
    ]),
    isBulkCurrentEvent() {
      return this.isBulkEvent;
    },
    modalTitle() {
      const title = this.ticketData.name || this.$t("common.no-ticket");
      return `
      ${this.$t("new-event.ticket-management.tickets.modal.title")}
      <span class='primary--text'>${title}</span>
    `;
    },
    batchItems() {
      const items = [{ text: "Sem lote", value: 0 }];
      for (let i = 1; i <= 10; i++) {
        items.push({ text: i.toString(), value: i });
      }
      return items;
    },
    hintBlockTickets() {
      const remainingTickets =
        (this.ticketData.quantity || 0) - (this.ticketData.blockQuantity || 0);

      return (
        this.$t(
          "new-event.ticket-management.tickets.modal.hint-block-tickets"
        ) +
        " " +
        remainingTickets
      );
    },

    commissionType() {
      return [
        {
          item: this.$t("new-event.settings.comission-type.comission"),
          value: "COMISSAO",
        },
        {
          item: this.$t("new-event.settings.comission-type.convenience"),
          value: "CONVENIENCIA",
        },
      ];
    },
    typeTax() {
      return [
        {
          item: this.$t("new-event.settings.type-tax.percent"),
          value: "percent",
        },
        {
          item: this.$t("new-event.settings.type-tax.value"),
          value: "value",
        },
      ];
    },
    status() {
      return [
        {
          item: this.$t("new-event.settings.status.active"),
          value: "ATIVO",
        },
        {
          item: this.$t("new-event.settings.status.inactive"),
          value: "INATIVO",
        },
        {
          item: this.$t("new-event.settings.status.sold-out"),
          value: "ESGOTADO",
        },
      ];
    },
    selectedLocales() {
      return this.getSelectedLocales;
    },
    locale() {
      return this.$i18n.locale;
    },
    formattedEventDescriptions() {
      const defaultDescription = `<${this.locale}>${
        this.descriptionDefault || ""
      }</${this.locale}>`;

      if (this.selectedLocales.length === 0) {
        return defaultDescription;
      }
      const allLocales = this.selectedLocales
        .map(selectedLocale => {
          const description =
            this.descriptionLocales[selectedLocale]?.trim() || "";
          return `<${selectedLocale}>${description}</${selectedLocale}>`;
        })
        .join("");

      return defaultDescription + allLocales;
    },
    eventWithoutCurrentEvent() {
      return this.getTicketList.filter(
        item => item.idproduto_variacao !== this.ticketData.idproduto_variacao
      );
    },
  },
  methods: {
    ...mapActions([
      "addAndRemoveTemplateWEB",
      "addAndRemoveTemplatePOS",
      "updateEvent",
    ]),
    handleClose() {
      this.ticketData = {};
      this.nameLocales = {};
      this.descriptionLocales = {};
      this.descriptionDefault = "";
      this.$emit("close");
    },
    async handleSave() {
      if (!this.$refs.form.validate()) {
        return;
      }
      this.updateDisabled();
      const data = {
        ...this.ticketData,
        nameLocales: this.nameLocales,
        description: this.formattedEventDescriptions,
      };
      await this.$emit("update:eventData", data);
      if (!this.isBulkEvent) {
        await this.updateEvent();
      }
      this.descriptionLocales = {};
      this.descriptionDefault = "";
    },
    allowedPeriodEndSaleDates(date) {
      const selectedDate = new Date(date);
      const startDate = new Date(this.ticketData.salesPeriodStart);
      if (isNaN(startDate.getTime())) {
        return true;
      }
      return selectedDate >= startDate;
    },
    formatItemText(item) {
      return `${item.name} - Lote ${item.batch}`;
    },
    accessPeriodStartFormattedDate(formatteddatetime) {
      this.ticketData.accessPeriodStart = formatteddatetime;
    },
    accessPeriodEndFormattedDate(formatteddatetime) {
      this.ticketData.accessPeriodEnd = formatteddatetime;
    },
    dateChangeBatchFormattedDate(formatteddatetime) {
      this.ticketData.dateChangeBatch = formatteddatetime;
    },
    saleStartFormattedDate(formatteddatetime) {
      this.ticketData.salesPeriodStart = formatteddatetime;
    },
    saleEndFormattedDate(formatteddatetime) {
      this.ticketData.salesPeriodEnd = formatteddatetime;
    },
    allowedChangeBatchDate(date) {
      const selectedDate = new Date(date);
      const accessPeriodEnd = new Date(this.ticketData.accessPeriodEnd);
      return selectedDate <= accessPeriodEnd;
    },
    handleFileChange(tipo, event) {
      const file = event.target.files[0] || null;
      const action = file ? "adicionar" : "remover";
      const id = this.ticketData.idproduto_variacao;

      if (tipo === "WEB") {
        this.addAndRemoveTemplateWEB({ action, file, id });
      } else if (tipo === "POS") {
        this.addAndRemoveTemplatePOS({ action, file, id });
      }
    },
    updateDisabled() {
      if (
        this.ticketData?.isCourtesy ||
        this.ticketData?.isReservation ||
        this.ticketData?.isGratuity
      ) {
        if (
          !this.ticketData?.disabled ||
          !this.ticketData?.disabled.includes("price")
        ) {
          this.ticketData.price = 0;
          if (typeof this.ticketData?.disabled === "string") {
            this.ticketData.disabled = ["price"];
          } else {
            if (!Array.isArray(this.ticketData?.disabled)) {
              this.ticketData.disabled = [];
            }
            this.ticketData?.disabled.push("price");
          }
        }
      } else {
        if (!Array.isArray(this.ticketData?.disabled)) {
          this.ticketData.disabled = [];
        }
        const index = this.ticketData?.disabled?.indexOf("price");
        if (index !== -1) {
          this.ticketData?.disabled?.splice(index, 1);
        }
      }
    },
    clearTaxes() {
      this.ticketData.salesChannel = "ECOMMERCE;IFRAME;LINK";
      this.ticketData.taxWeb = 0;
      this.ticketData.taxPos = 0;
      this.ticketData.minimumTax = 0;
      this.ticketData.processingTax = 0;
    },
    onGratuityChange() {
      this.ticketData.isCourtesy = false;
      this.ticketData.isReservation = false;
      this.clearTaxes();
    },
    onCourtesyChange() {
      this.ticketData.isGratuity = false;
      this.ticketData.isReservation = false;
      this.clearTaxes();
    },
    onReservationChange() {
      this.ticketData.isCourtesy = false;
      this.ticketData.isGratuity = false;
      this.clearTaxes();
    },
    onFacialChange(ticketFacial) {
      if (!ticketFacial) this.ticketData.ticketFacialRequired = false;
    },
    onFacialRequiredChange(ticketFacialRequired) {
      if (ticketFacialRequired) this.ticketData.ticketFacial = true;
    }
  },
  watch: {
    dialog() {
      if (this.dialog) {
        this.ticketData = { ...this.eventData };
        this.nameLocales = { ...this.eventData.nameLocales };
        if (this.ticketData?.description) {
          const descriptions = processEventName(this.ticketData.description);
          this.descriptionDefault = descriptions[this.locale] || "";
          this.descriptionLocales = descriptions || {};
        }
      } else {
        this.ticketData = {};
      }
    },
  },
};
</script>

<style scoped></style>
